import { useState, useRef, useEffect, useCallback } from "react";

// ─── THEME TOKENS (same as PythonPandas) ─────────────────────────────────────
const T = {
  bg:       "#0B0F1A",
  surface:  "#111827",
  card:     "#1A2235",
  border:   "#1E2D45",
  accent:   "#38BDF8",
  accentDim:"#0EA5E9",
  green:    "#34D399",
  yellow:   "#FCD34D",
  red:      "#F87171",
  purple:   "#A78BFA",
  orange:   "#FB923C",
  textPri:  "#F0F6FF",
  textSec:  "#94A3B8",
  textMute: "#4B5563",
  codeBg:   "#060D18",
  codeStr:  "#86EFAC",
  codeKw:   "#7DD3FC",
  codeFn:   "#FDE68A",
  codeCmt:  "#475569",
  codeNum:  "#F9A8D4",
};

// ─── SYNTAX HIGHLIGHT ─────────────────────────────────────────────────────────
function highlight(code) {
  const threadingBuiltins = ["threading","Thread","Lock","RLock","Semaphore","Event",
    "Queue","ThreadPoolExecutor","executor","t","t1","t2","t3","lock","lock1","lock2",
    "event","semaphore","q","daemon","target","args","name","join","start","acquire",
    "release","wait","set","clear","put","get","task_done","map","submit","result","results"];
  return code.split("\n").map((line, li) => {
    const tokens = [];
    let i = 0;
    while (i < line.length) {
      if (line[i] === "#") {
        tokens.push(<span key={i} style={{color:T.codeCmt}}>{line.slice(i)}</span>);
        i = line.length; continue;
      }
      if (line[i] === '"' || line[i] === "'") {
        const q = line[i]; let j = i+1;
        while (j < line.length && !(line[j]===q && line[j-1]!=="\\")) j++;
        tokens.push(<span key={i} style={{color:T.codeStr}}>{line.slice(i,j+1)}</span>);
        i = j+1; continue;
      }
      if (/\d/.test(line[i]) && (i===0||!/\w/.test(line[i-1]))) {
        let j = i;
        while (j<line.length && /[\d._]/.test(line[j])) j++;
        tokens.push(<span key={i} style={{color:T.codeNum}}>{line.slice(i,j)}</span>);
        i = j; continue;
      }
      if (/[a-zA-Z_]/.test(line[i])) {
        let j = i;
        while (j<line.length && /\w/.test(line[j])) j++;
        const word = line.slice(i,j);
        const kws = ["import","from","as","def","class","return","for","in","if","else","elif",
          "and","or","not","True","False","None","lambda","with","print","pass","while",
          "global","break","continue","yield","async","await","try","except","raise"];
        let color = T.textPri;
        if (kws.includes(word)) color = T.codeKw;
        else if (threadingBuiltins.includes(word)) color = T.accent;
        tokens.push(<span key={i} style={{color}}>{word}</span>);
        i = j; continue;
      }
      const ops = "()[]{}=<>+-*/%.,!:";
      if (ops.includes(line[i])) {
        tokens.push(<span key={i} style={{color:"#93C5FD"}}>{line[i]}</span>);
        i++; continue;
      }
      tokens.push(<span key={i} style={{color:T.textPri}}>{line[i]}</span>);
      i++;
    }
    return <div key={li} style={{minHeight:"1.4em"}}>{tokens}</div>;
  });
}

// ─── REUSABLE COMPONENTS (same pattern as PythonPandas) ──────────────────────
function CodeBlock({ code, label="python" }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{marginBottom:16,borderRadius:10,overflow:"hidden",border:`1px solid ${T.border}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
        background:"#0D1929",padding:"6px 14px",borderBottom:`1px solid ${T.border}`}}>
        <span style={{fontSize:11,color:T.accent,fontFamily:"monospace",letterSpacing:.5}}>{label}</span>
        <button onClick={()=>{navigator.clipboard.writeText(code);setCopied(true);setTimeout(()=>setCopied(false),1500);}}
          style={{fontSize:11,color:copied?T.green:T.textSec,background:"none",border:"none",cursor:"pointer",
            padding:"2px 8px",borderRadius:4,transition:"color .2s"}}>
          {copied ? "✓ copied" : "copy"}
        </button>
      </div>
      <div style={{background:T.codeBg,padding:"14px 16px",overflowX:"auto",
        fontFamily:"'JetBrains Mono','Fira Mono',monospace",fontSize:12.5,lineHeight:1.65}}>
        {highlight(code)}
      </div>
    </div>
  );
}

function Concept({ children }) {
  return (
    <div style={{background:T.card,borderLeft:`3px solid ${T.accent}`,borderRadius:"0 8px 8px 0",
      padding:"12px 16px",marginBottom:14,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
      {children}
    </div>
  );
}

function Analogy({ children }) {
  return (
    <div style={{background:"#0F1E2E",border:"1px solid #1E3A52",borderRadius:8,
      padding:"12px 16px",marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:600,color:T.accent,letterSpacing:1,marginBottom:6,textTransform:"uppercase"}}>
        Analogy
      </div>
      <div style={{fontSize:13,color:T.textSec,lineHeight:1.7}}>{children}</div>
    </div>
  );
}

function WarnBox({ items }) {
  return (
    <div style={{background:"#1C1506",border:"1px solid #78350F",borderRadius:8,
      padding:"12px 16px",marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:600,color:T.yellow,letterSpacing:1,marginBottom:8,textTransform:"uppercase"}}>
        Watch Out
      </div>
      {items.map((it,i)=>(
        <div key={i} style={{fontSize:13,color:"#D4B483",marginBottom:5,lineHeight:1.5}}>{it}</div>
      ))}
    </div>
  );
}

function InfoBox({ title, color="#38BDF8", bgColor="#071929", borderColor="#1E3A52", children }) {
  return (
    <div style={{background:bgColor,border:`1px solid ${borderColor}`,borderRadius:8,padding:"12px 16px",marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:600,color,letterSpacing:1,marginBottom:8,textTransform:"uppercase"}}>{title}</div>
      <div style={{fontSize:13,color:T.textSec,lineHeight:1.7}}>{children}</div>
    </div>
  );
}

function InterviewQ({ items }) {
  const [open, setOpen] = useState({});
  return (
    <div style={{background:"#071929",border:"1px solid #1E3A52",borderRadius:8,padding:"12px 16px",marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:600,color:"#60A5FA",letterSpacing:1,marginBottom:10,textTransform:"uppercase"}}>
        Interview Questions
      </div>
      {items.map((item,i)=>(
        <div key={i} style={{marginBottom:8}}>
          <div onClick={()=>setOpen(o=>({...o,[i]:!o[i]}))}
            style={{cursor:"pointer",display:"flex",gap:8,alignItems:"flex-start",
              fontSize:13,color:T.textPri,fontWeight:500,lineHeight:1.5}}>
            <span style={{color:"#60A5FA",minWidth:16,marginTop:1}}>{open[i]?"▾":"▸"}</span>
            <span>{item.q}</span>
          </div>
          {open[i] && (
            <div style={{fontSize:12.5,color:T.textSec,marginTop:6,paddingLeft:24,lineHeight:1.65}}>{item.a}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function ASCII({ children }) {
  return (
    <pre style={{background:"#07111E",border:`1px solid ${T.border}`,borderRadius:8,
      padding:"12px 16px",marginBottom:14,fontSize:12,color:"#7DD3FC",
      lineHeight:1.6,overflowX:"auto",fontFamily:"'JetBrains Mono','Fira Mono',monospace"}}>
      {children}
    </pre>
  );
}

// ─── LIVE DEMO: Race Condition ────────────────────────────────────────────────
function RaceDemo() {
  const [log, setLog] = useState([]);
  const [counter, setCounter] = useState(null);
  const [running, setRunning] = useState(false);
  const tokenRef = useRef(0);

  const run = useCallback((useLock) => {
    tokenRef.current++;
    const myToken = tokenRef.current;
    setRunning(true); setCounter(null); setLog([]);
    let shared = 0, lockHeld = false, remaining = 2;
    const TIMES = 5;
    const lines = [];
    function addLine(s) { lines.push(s); setLog([...lines]); }

    function threadRun(name, onDone) {
      let i = 0;
      function step() {
        if (tokenRef.current !== myToken) return;
        if (i >= TIMES) { onDone(); return; }
        i++;
        tryOp();
      }
      function tryOp() {
        if (tokenRef.current !== myToken) return;
        if (useLock && lockHeld) { setTimeout(tryOp, 6); return; }
        if (useLock) lockHeld = true;
        const read = shared;
        setTimeout(() => {
          if (tokenRef.current !== myToken) return;
          shared = read + 1;
          addLine(`${name}: read ${read} → wrote ${shared}`);
          if (useLock) lockHeld = false;
          setTimeout(step, Math.random() * 30);
        }, Math.random() * 30);
      }
      step();
    }
    function onOneDone() {
      remaining--;
      if (remaining === 0) {
        setCounter(shared);
        setRunning(false);
      }
    }
    threadRun("Thread-A", onOneDone);
    threadRun("Thread-B", onOneDone);
  }, []);

  const expected = 10;
  return (
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:10,padding:16,marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:600,color:T.purple,letterSpacing:1,marginBottom:12,textTransform:"uppercase"}}>
        Live Demo — Race Condition (5 increments × 2 threads)
      </div>
      <div style={{display:"flex",gap:10,marginBottom:12,flexWrap:"wrap"}}>
        <button disabled={running} onClick={()=>run(false)}
          style={{fontSize:12,padding:"6px 14px",borderRadius:6,border:`1px solid ${T.red}`,
            background:"#1a0a0a",color:T.red,cursor:running?"not-allowed":"pointer",opacity:running?0.5:1}}>
          Run without lock
        </button>
        <button disabled={running} onClick={()=>run(true)}
          style={{fontSize:12,padding:"6px 14px",borderRadius:6,border:`1px solid ${T.green}`,
            background:"#0a1a10",color:T.green,cursor:running?"not-allowed":"pointer",opacity:running?0.5:1}}>
          Run with lock
        </button>
        {counter !== null && (
          <span style={{fontSize:13,fontWeight:600,padding:"6px 14px",borderRadius:6,
            background:counter===expected?"#0a1a10":"#1a0a0a",
            color:counter===expected?T.green:T.red,border:`1px solid ${counter===expected?T.green:T.red}`}}>
            counter: {counter} (expected {expected}) {counter===expected?"✓":"✗ race!"}
          </span>
        )}
      </div>
      <div style={{background:T.codeBg,borderRadius:8,padding:"10px 14px",minHeight:80,
        fontFamily:"'JetBrains Mono',monospace",fontSize:12,color:T.textSec,lineHeight:1.7}}>
        {log.length===0 ? <span style={{color:T.textMute}}>Press a button to start…</span>
          : log.map((l,i)=><div key={i}>{l}</div>)}
      </div>
    </div>
  );
}

// ─── LIVE DEMO: Deadlock ──────────────────────────────────────────────────────
function DeadlockDemo() {
  const [log, setLog] = useState([]);
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const tokenRef = useRef(0);

  function run(safe) {
    tokenRef.current++;
    const myToken = tokenRef.current;
    setRunning(true); setStatus(null);
    const lines = [];
    function add(s, c) { lines.push({s,c}); setLog([...lines]); }

    add("Task-1 acquires lock1…", T.textSec);
    add("Task-2 acquires lock2…", T.textSec);
    setTimeout(() => {
      if (tokenRef.current !== myToken) return;
      if (safe) {
        add("Task-1 acquires lock2… ✓", T.green);
        setTimeout(() => {
          if (tokenRef.current !== myToken) return;
          add("Task-1 done, releases both", T.green);
          add("Task-2 acquires lock1… ✓", T.green);
          add("Task-2 done — No deadlock!", T.green);
          setStatus("safe"); setRunning(false);
        }, 500);
      } else {
        add("Task-1 waits for lock2 (held by Task-2)…", T.yellow);
        add("Task-2 waits for lock1 (held by Task-1)…", T.yellow);
        setTimeout(() => {
          if (tokenRef.current !== myToken) return;
          add("💀 DEADLOCK — both tasks stuck forever", T.red);
          setStatus("dead"); setRunning(false);
        }, 500);
      }
    }, 500);
  }

  return (
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:10,padding:16,marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:600,color:T.purple,letterSpacing:1,marginBottom:12,textTransform:"uppercase"}}>
        Live Demo — Deadlock
      </div>
      <div style={{display:"flex",gap:10,marginBottom:12,flexWrap:"wrap"}}>
        <button disabled={running} onClick={()=>run(false)}
          style={{fontSize:12,padding:"6px 14px",borderRadius:6,border:`1px solid ${T.red}`,
            background:"#1a0a0a",color:T.red,cursor:running?"not-allowed":"pointer",opacity:running?0.5:1}}>
          Opposite order (risky)
        </button>
        <button disabled={running} onClick={()=>run(true)}
          style={{fontSize:12,padding:"6px 14px",borderRadius:6,border:`1px solid ${T.green}`,
            background:"#0a1a10",color:T.green,cursor:running?"not-allowed":"pointer",opacity:running?0.5:1}}>
          Same order (safe)
        </button>
        <button disabled={running} onClick={()=>{setLog([]);setStatus(null);}}
          style={{fontSize:12,padding:"6px 14px",borderRadius:6,border:`1px solid ${T.border}`,
            color:T.textSec,cursor:"pointer"}}>
          Reset
        </button>
      </div>
      <div style={{background:T.codeBg,borderRadius:8,padding:"10px 14px",minHeight:80,
        fontFamily:"'JetBrains Mono',monospace",fontSize:12,lineHeight:1.7}}>
        {log.length===0 ? <span style={{color:T.textMute}}>Press a button to simulate…</span>
          : log.map((l,i)=><div key={i} style={{color:l.c}}>{l.s}</div>)}
      </div>
    </div>
  );
}

// ─── LIVE DEMO: Semaphore ─────────────────────────────────────────────────────
function SemaphoreDemo() {
  const [slots, setSlots] = useState([null,null,null]);
  const [status, setStatus] = useState("");
  const [running, setRunning] = useState(false);
  const tokenRef = useRef(0);

  function runDemo() {
    tokenRef.current++;
    const myToken = tokenRef.current;
    setRunning(true);
    const current = [null,null,null];
    const queue = ["U0","U1","U2","U3","U4","U5"];
    let done = 0;
    setStatus("6 users requesting access…");

    function tryAdmit() {
      if (tokenRef.current !== myToken) return;
      for (let i = 0; i < 3; i++) {
        if (!current[i] && queue.length > 0) {
          const u = queue.shift();
          current[i] = u;
          setSlots([...current]);
          (function(idx) {
            setTimeout(() => {
              if (tokenRef.current !== myToken) return;
              current[idx] = null;
              setSlots([...current]);
              done++;
              if (queue.length > 0) setStatus(`${queue.length} still waiting…`);
              else if (done === 6) { setStatus("All 6 users served."); setRunning(false); }
              else setStatus("Finishing…");
              tryAdmit();
            }, 1400);
          })(i);
        }
      }
    }
    setSlots([...current]);
    tryAdmit();
  }

  return (
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:10,padding:16,marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:600,color:T.purple,letterSpacing:1,marginBottom:12,textTransform:"uppercase"}}>
        Live Demo — Semaphore(3): max 3 concurrent users
      </div>
      <div style={{display:"flex",gap:10,marginBottom:12}}>
        {slots.map((s,i)=>(
          <div key={i} style={{width:72,height:56,borderRadius:8,display:"flex",alignItems:"center",
            justifyContent:"center",fontSize:12,fontFamily:"monospace",
            border:`1px solid ${s?T.accent:T.border}`,
            background:s?"#071929":T.surface,color:s?T.accent:T.textMute}}>
            {s || "empty"}
          </div>
        ))}
      </div>
      <button disabled={running} onClick={runDemo}
        style={{fontSize:12,padding:"6px 14px",borderRadius:6,border:`1px solid ${T.accent}`,
          background:"#071929",color:T.accent,cursor:running?"not-allowed":"pointer",
          marginBottom:10,opacity:running?0.5:1}}>
        Send 6 users
      </button>
      {status && <div style={{fontSize:12,color:T.textSec,marginTop:6}}>{status}</div>}
    </div>
  );
}

// ─── LIVE DEMO: Queue ─────────────────────────────────────────────────────────
function QueueDemo() {
  const [items, setItems] = useState([]);
  const [n, setN] = useState(0);

  return (
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:10,padding:16,marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:600,color:T.purple,letterSpacing:1,marginBottom:12,textTransform:"uppercase"}}>
        Live Demo — Producer / Consumer Queue (FIFO)
      </div>
      <div style={{display:"flex",gap:10,marginBottom:12}}>
        <button onClick={()=>{setItems(prev=>[...prev,n]);setN(c=>c+1);}}
          style={{fontSize:12,padding:"6px 14px",borderRadius:6,border:`1px solid ${T.green}`,
            background:"#0a1a10",color:T.green,cursor:"pointer"}}>
          Produce item
        </button>
        <button onClick={()=>setItems(prev=>prev.slice(1))} disabled={items.length===0}
          style={{fontSize:12,padding:"6px 14px",borderRadius:6,border:`1px solid ${T.orange}`,
            background:"#1a1006",color:T.orange,cursor:items.length===0?"not-allowed":"pointer",
            opacity:items.length===0?0.5:1}}>
          Consume item
        </button>
      </div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap",minHeight:40,alignItems:"center"}}>
        {items.length===0
          ? <span style={{fontSize:12,color:T.textMute,fontFamily:"monospace"}}>queue empty</span>
          : items.map((it,i)=>(
            <span key={i} style={{fontSize:12,padding:"5px 12px",borderRadius:6,
              background:"#071929",color:T.accent,border:`1px solid ${T.accent}44`,fontFamily:"monospace"}}>
              item {it}
            </span>
          ))
        }
      </div>
    </div>
  );
}

// ─── QUIZ ─────────────────────────────────────────────────────────────────────
const QUIZ = [
  {q:"What does thread.start() do?",opts:["Blocks until thread finishes","Begins running the thread's target","Creates the thread object only","Deletes the thread"],a:1},
  {q:"Threading gives the biggest speed win for which kind of task?",opts:["Heavy CPU math","I/O-bound waiting (network, disk, API)","Sorting huge arrays","Image compression"],a:1},
  {q:"What does t.join() do?",opts:["Starts the thread","Kills the thread","Waits for the thread to finish","Renames the thread"],a:2},
  {q:"Without calling join(), what can happen?",opts:["The program crashes","Main code continues before the thread finishes","The thread never starts","Threads merge into one"],a:1},
  {q:"Why does counter += 1 cause race conditions?",opts:["It is actually read, add, write as 3 separate steps","Python forbids += in threads","It only works with floats","It automatically locks"],a:0},
  {q:"What does a Lock guarantee?",opts:["Threads run faster","Only one thread enters the critical section at a time","Threads run in a fixed order","No thread ever waits"],a:1},
  {q:"threading.Lock() behaves like a…",opts:["Semaphore(10)","Mutex","Daemon","Queue"],a:1},
  {q:"What causes a deadlock?",opts:["Same lock order everywhere","Two threads each holding one lock and waiting on the other's","Using too many daemon threads","Calling join() twice"],a:1},
  {q:"When should you prefer RLock over Lock?",opts:["When a locked function calls another locked function in the same thread","When you need unlimited threads","When you need no locking","When using a Queue"],a:0},
  {q:"A Semaphore(3) allows…",opts:["Exactly 3 threads to ever run","Up to 3 threads to hold it at once","3 seconds of waiting","Only 1 thread, 3 times"],a:1},
  {q:"threading.Event() is best used for…",opts:["Sharing a counter safely","Signalling one thread to start after another says go","Limiting concurrent access","Reentrant locking"],a:1},
  {q:"Why prefer queue.Queue over a plain shared list?",opts:["It's faster at math","It's already thread-safe, no manual locks needed","It has no size limit","It runs on multiple processes"],a:1},
  {q:"What happens to a daemon thread when the main program exits?",opts:["It keeps running","It is killed automatically","It becomes the main thread","It throws an error"],a:1},
  {q:"What does ThreadPoolExecutor.map() do?",opts:["Runs a function across many inputs using a fixed pool of workers","Draws a map of all threads","Locks every thread","Only works with one input"],a:0},
  {q:"Best fix for a deadlock risk between lock1 and lock2?",opts:["Use more threads","Always acquire locks in the same order everywhere","Never release locks","Use daemon threads instead"],a:1},
];

function QuizSection() {
  const [answers, setAnswers] = useState({});
  const [checked, setChecked] = useState(false);

  const score = checked
    ? QUIZ.reduce((s,q,i)=>s+(answers[i]===q.a?1:0), 0)
    : 0;

  return (
    <div>
      <div style={{marginBottom:16}}>
        <div style={{fontSize:11,fontWeight:600,color:T.accent,letterSpacing:1.5,
          textTransform:"uppercase",marginBottom:8}}>Self-Test</div>
        <h1 style={{fontSize:22,fontWeight:700,color:T.textPri,margin:"0 0 8px"}}>Threading Quiz</h1>
        <p style={{fontSize:13.5,color:T.textSec,margin:0,lineHeight:1.6}}>
          {QUIZ.length} questions — select an answer then click Check.
        </p>
      </div>

      {checked && (
        <div style={{background:score===QUIZ.length?"#0a1a10":"#071929",
          border:`1px solid ${score===QUIZ.length?T.green:T.accent}`,
          borderRadius:10,padding:"14px 18px",marginBottom:20,
          display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:28}}>{score===QUIZ.length?"🏆":score>=10?"🎯":"📚"}</span>
          <div>
            <div style={{fontSize:16,fontWeight:700,color:score===QUIZ.length?T.green:T.textPri}}>
              Score: {score} / {QUIZ.length}
            </div>
            <div style={{fontSize:12.5,color:T.textSec,marginTop:2}}>
              {score===QUIZ.length?"Perfect — you know threading cold!"
                :score>=10?"Strong — revisit the missed topics"
                :"Keep practising — retry after reviewing the sections"}
            </div>
          </div>
        </div>
      )}

      {QUIZ.map((item,qi)=>{
        const chosen = answers[qi];
        const correct = item.a;
        return (
          <div key={qi} style={{background:T.card,border:`1px solid ${T.border}`,
            borderRadius:10,padding:"14px 16px",marginBottom:10}}>
            <div style={{fontSize:13.5,fontWeight:600,color:T.textPri,marginBottom:10,lineHeight:1.5}}>
              {qi+1}. {item.q}
            </div>
            {item.opts.map((opt,oi)=>{
              let bg = "transparent", col = T.textSec, borderC = "transparent";
              if (checked) {
                if (oi===correct) { bg="#0a1a10"; col=T.green; borderC=T.green; }
                else if (chosen===oi && oi!==correct) { bg="#1a0a0a"; col=T.red; borderC=T.red; }
              } else if (chosen===oi) { bg="#071929"; col=T.accent; borderC=T.accent; }
              return (
                <label key={oi} style={{display:"flex",alignItems:"center",gap:10,
                  padding:"7px 10px",borderRadius:7,marginBottom:4,cursor:"pointer",
                  background:bg,border:`1px solid ${borderC}`,transition:"all .15s"}}>
                  <input type="radio" name={`q${qi}`}
                    checked={chosen===oi} onChange={()=>!checked&&setAnswers(a=>({...a,[qi]:oi}))}
                    style={{accentColor:T.accent}} />
                  <span style={{fontSize:13,color:col}}>{opt}</span>
                  {checked && oi===correct && <span style={{marginLeft:"auto",color:T.green,fontSize:12}}>✓</span>}
                </label>
              );
            })}
          </div>
        );
      })}

      <div style={{display:"flex",gap:10,marginTop:16}}>
        <button disabled={checked || Object.keys(answers).length<QUIZ.length} onClick={()=>setChecked(true)}
          style={{fontSize:13,padding:"8px 20px",borderRadius:8,border:`1px solid ${T.accent}`,
            background:"#071929",color:T.accent,cursor:"pointer",
            opacity:(!checked && Object.keys(answers).length===QUIZ.length)?1:0.4}}>
          Check answers
        </button>
        <button onClick={()=>{setAnswers({});setChecked(false);}}
          style={{fontSize:13,padding:"8px 20px",borderRadius:8,border:`1px solid ${T.border}`,
            color:T.textSec,background:"none",cursor:"pointer"}}>
          Reset
        </button>
      </div>
    </div>
  );
}

// ─── SECTION CONTENT ─────────────────────────────────────────────────────────
function S1() { return (
  <div>
    <Concept>A thread is an independent path of execution inside your program. A normal script runs one instruction after another. Threads let two or more of these paths exist in the same process, with the OS switching between them so they appear to run simultaneously.</Concept>
    <Analogy>A single-threaded program is one person doing every task alone, one at a time. A multi-threaded program is several people working in the same kitchen at once — all inside the same building (process), each on their own task.</Analogy>
    <ASCII>{`Process (one building)
┌───────────────────────────────────────────┐
│  Thread-1 → reads file                    │
│  Thread-2 → calls API         (concurrent)│
│  Thread-3 → writes log                    │
│  shared memory / globals ← all can access │
└───────────────────────────────────────────┘`}</ASCII>
    <CodeBlock code={`import threading

def task():
    print("Task running in:", threading.current_thread().name)

t1 = threading.Thread(target=task)
t2 = threading.Thread(target=task)

t1.start()
t2.start()
t1.join()
t2.join()

print("All tasks completed")`} />
    <InfoBox title="Key Point">Output order between t1 and t2 is not guaranteed — both run independently. The OS scheduler decides which runs when.</InfoBox>
    <InterviewQ items={[
      {q:"What is the difference between a process and a thread?",a:"A process is a completely isolated program with its own memory. Threads share memory within the same process. Creating threads is cheaper than creating processes, but shared memory means threads must coordinate access carefully."},
      {q:"Does Python truly run threads in parallel?",a:"Not for CPU-bound work — the GIL (Global Interpreter Lock) allows only one thread to execute Python bytecode at a time. But for I/O-bound work (network, disk), threads release the GIL while waiting, so multiple threads effectively run concurrently."},
    ]} />
  </div>
);}

function S2() { return (
  <div>
    <Concept>Threading pays off when a program spends most of its time waiting rather than computing: downloading files, calling APIs, reading or writing files, database queries. While one thread waits on I/O, another keeps working. For heavy CPU math, use <code style={{color:T.accent}}>multiprocessing</code> instead — threads can't bypass the GIL there.</Concept>
    <Analogy>A good waiter doesn't stand at one table waiting for food to arrive. They take another order while the kitchen cooks. Threads let your program start the next task while waiting on I/O — the waiting thread releases the GIL and lets others run.</Analogy>
    <ASCII>{`I/O-bound (threads shine):       CPU-bound (use multiprocessing):
  ┌──────┐  waiting on network         ┌──────┐  crunching numbers
  │ T-1  │░░░░░░░░░░░░░░░░            │ T-1  │████████████████
  │ T-2  │      ░░░░░░░░░░░░          │ T-2  │████████████████
  │ T-3  │    ░░░░░░░░░░░░            │ T-3  │████████████████
  └──────┘  ░ = waiting, runs others  └──────┘  GIL blocks all but 1`}</ASCII>
    <CodeBlock code={`# Good fit for threads — I/O-bound:
#   downloading files, calling APIs,
#   reading/writing files, writing logs,
#   database queries, waiting on sockets

# Poor fit — CPU-bound:
#   heavy math, image processing,
#   number crunching → use multiprocessing

# Example: 5 API calls sequentially = 5s
# Example: 5 API calls with threads  ≈ 1s`} />
    <WarnBox items={[
      "Threading does NOT speed up CPU-bound code — the GIL limits it to one thread at a time for Python bytecode.",
      "Use concurrent.futures.ProcessPoolExecutor for CPU-bound parallelism.",
    ]} />
    <InterviewQ items={[
      {q:"When would you pick threads vs multiprocessing vs asyncio?",a:"Threads: I/O-bound with blocking libraries (requests, psycopg2). Asyncio: I/O-bound with async-native libraries (aiohttp, asyncpg) — single thread, event loop. Multiprocessing: CPU-bound work — each process has its own GIL-free interpreter."},
    ]} />
  </div>
);}

function S3() { return (
  <div>
    <Concept><code style={{color:T.accent}}>start()</code> launches a thread so it runs alongside the rest of your program. <code style={{color:T.accent}}>join()</code> blocks the calling thread until the target thread finishes. Without <code style={{color:T.accent}}>join()</code>, your main thread may exit before the worker threads complete.</Concept>
    <Analogy>start() is telling a chef to begin cooking. join() is standing at the kitchen door until that chef says the dish is ready. Without join() you walk out of the kitchen and the chef may still be cooking.</Analogy>
    <CodeBlock code={`import threading
import time

def cook_food(food):
    print(f"Started cooking {food}")
    time.sleep(2)                      # simulate I/O wait
    print(f"Finished cooking {food}")

t1 = threading.Thread(target=cook_food, args=("Pizza",))
t2 = threading.Thread(target=cook_food, args=("Burger",))

t1.start()   # both start cooking at the same time
t2.start()

t1.join()    # wait for pizza
t2.join()    # wait for burger

print("Kitchen closed")   # guaranteed last line`} />
    <CodeBlock label="pass arguments" code={`import threading

def greet(name, times):
    for _ in range(times):
        print(f"Hello, {name}!")

t = threading.Thread(target=greet, args=("Alice", 3))
t.start()
t.join()`} />
    <InterviewQ items={[
      {q:"What happens if you call start() twice on the same thread?",a:"RuntimeError: threads can only be started once. Create a new Thread object if you need to run the same function again."},
      {q:"Can you join() a thread before it has started?",a:"No — calling join() before start() raises a RuntimeError. Always start() before join()."},
    ]} />
  </div>
);}

function S4() { return (
  <div>
    <Concept>If you never call <code style={{color:T.accent}}>join()</code>, the main thread keeps going immediately after <code style={{color:T.accent}}>start()</code>. The main program can finish printing before the background thread completes — they race independently. This is sometimes intentional (fire-and-forget), but usually a bug.</Concept>
    <CodeBlock code={`import threading
import time

def slow_task():
    time.sleep(2)
    print("Task done")          # may print AFTER "Main finished"

t = threading.Thread(target=slow_task)
t.start()
# No join() here!

print("Main program finished")  # prints immediately`} />
    <CodeBlock label="expected vs actual output" code={`# With join():
#   Main program finished
#   Task done            ← guaranteed order

# Without join() — race:
#   Main program finished
#   Task done            ← OR reversed, timing-dependent`} />
    <WarnBox items={[
      "Forgetting join() is a common bug — the program appears to work but threads may be silently interrupted on exit.",
      "If you intentionally want fire-and-forget background work, use daemon=True so the thread is auto-killed on exit.",
    ]} />
    <InterviewQ items={[
      {q:"When is NOT calling join() intentional?",a:"When you want a background task that should run alongside the program without blocking the main thread. In that case, set daemon=True so the thread is automatically killed when the program exits (see Daemon Thread section)."},
    ]} />
  </div>
);}

function S5() { return (
  <div>
    <Concept>Every thread can be given a name. <code style={{color:T.accent}}>threading.current_thread().name</code> returns the name of the currently executing thread. Naming threads makes log messages and debug output far more readable once you have several running.</Concept>
    <Analogy>Name tags on kitchen staff — once you have Chef-1, Chef-2, and Chef-3 all running around, "ERROR in thread" is useless, but "ERROR in Chef-DB-Writer" tells you exactly who to investigate.</Analogy>
    <CodeBlock code={`import threading

def task():
    name = threading.current_thread().name
    print(f"Running in: {name}")

t1 = threading.Thread(target=task, name="Chef-1")
t2 = threading.Thread(target=task, name="Chef-2")

t1.start()
t2.start()
t1.join()
t2.join()

# Output (any order):
# Running in: Chef-1
# Running in: Chef-2`} />
    <CodeBlock label="thread info" code={`import threading

def worker():
    t = threading.current_thread()
    print(f"name={t.name}  daemon={t.daemon}  id={t.ident}")

t = threading.Thread(target=worker, name="MyWorker")
t.start()
t.join()

print("Active threads:", threading.active_count())
print("All threads:", [t.name for t in threading.enumerate()])`} />
    <InterviewQ items={[
      {q:"What is threading.enumerate()?",a:"Returns a list of all Thread objects currently alive. Useful for debugging to see exactly which threads are still running."},
      {q:"What is threading.active_count()?",a:"Returns the number of Thread objects currently alive (including the main thread). Useful to detect thread leaks."},
    ]} />
  </div>
);}

function S6() { return (
  <div>
    <Concept><code style={{color:T.accent}}>counter += 1</code> is three separate steps: read counter → add 1 → write counter. If two threads interleave these steps, one thread's update can overwrite another — the final value ends up lower than expected. This is a race condition: correctness depends on unpredictable timing.</Concept>
    <Analogy>Two people each read the same whiteboard number (say, 5). Both add 1 in their head. Both walk back and write 6. One increment is silently lost — the board should say 7.</Analogy>
    <ASCII>{`Thread-A            Thread-B            shared counter
────────────────────────────────────────────────────
read 5                                      5
                    read 5                  5
write 6 (5+1)                               6
                    write 6 (5+1)           6  ← LOST increment!
                                            expected: 7`}</ASCII>
    <CodeBlock code={`import threading

counter = 0

def increment():
    global counter
    for _ in range(100000):
        counter += 1    # NOT atomic — read/add/write can interleave

t1 = threading.Thread(target=increment)
t2 = threading.Thread(target=increment)
t1.start(); t2.start()
t1.join(); t2.join()

print(counter)  # expected 200000 — actual is often less!`} />
    <RaceDemo />
    <InterviewQ items={[
      {q:"Is counter += 1 atomic in Python?",a:"No. Even though Python has the GIL, the GIL only prevents two threads from running bytecode simultaneously — it can still be interrupted between the LOAD, BINARY_ADD, and STORE bytecode instructions that make up +=. Use a Lock to make the whole operation atomic."},
      {q:"What other operations are NOT thread-safe in Python?",a:"list.sort(), dict update if reading + writing separately, any read-modify-write pattern. Reading a single reference is safe (references are atomic), but anything involving multiple steps is not."},
    ]} />
  </div>
);}

function S7() { return (
  <div>
    <Concept>A <code style={{color:T.accent}}>Lock</code> forces only one thread at a time through a critical section. <code style={{color:T.accent}}>acquire()</code> takes the lock (blocks if another thread holds it), <code style={{color:T.accent}}>release()</code> gives it back. <code style={{color:T.accent}}>with lock:</code> does both automatically and is always preferred.</Concept>
    <Analogy>A Lock is a kitchen token. Only whoever holds the token may touch the shared counter — everyone else waits. When the token is returned, the next waiting thread picks it up.</Analogy>
    <CodeBlock code={`import threading

counter = 0
lock = threading.Lock()

def increment():
    global counter
    for _ in range(100000):
        with lock:          # acquire on enter, release on exit (even on exception)
            counter += 1   # only one thread here at a time

threads = [threading.Thread(target=increment) for _ in range(5)]
for t in threads: t.start()
for t in threads: t.join()

print("Final counter:", counter)   # always 500000`} />
    <CodeBlock label="manual acquire/release (avoid)" code={`# Equivalent but error-prone — always prefer 'with lock:'
lock.acquire()
try:
    counter += 1
finally:
    lock.release()   # MUST release even if an exception occurs`} />
    <InfoBox title="Rule of Thumb" color={T.green} bgColor="#0a1a10" borderColor="#1E3A52">
      Use <code style={{color:T.accent}}>with lock:</code> 100% of the time. It's shorter, safer (releases on exception), and readable.
    </InfoBox>
    <InterviewQ items={[
      {q:"What happens if a thread never releases a Lock?",a:"Every other thread trying to acquire that lock will block forever — a deadlock. The 'with' statement prevents this by always releasing on exit."},
      {q:"Can the same thread acquire a Lock it already holds?",a:"No — a regular Lock will block forever (deadlock) if the same thread tries to acquire it again. Use RLock for that case."},
    ]} />
  </div>
);}

function S8() { return (
  <div>
    <Concept>In Python, <code style={{color:T.accent}}>threading.Lock()</code> implements a mutex (mutual exclusion): only one thread may be inside the protected region — the critical section — at a time. The terms Lock and mutex describe the same concept; Python just calls its implementation Lock.</Concept>
    <ASCII>{`Mutex / Lock concept:
┌─────────────────────────────────────────┐
│  Thread-A: with lock:  → ENTERS         │
│      critical section (only A here)     │
│  Thread-B: with lock:  → BLOCKED        │
│      waiting... waiting...              │
│  Thread-A exits with block → RELEASES   │
│  Thread-B: with lock:  → NOW ENTERS     │
└─────────────────────────────────────────┘`}</ASCII>
    <CodeBlock code={`import threading

lock = threading.Lock()
shared_data = 0

def safe_update():
    global shared_data
    with lock:
        # critical section — only one thread at a time
        shared_data += 1`} />
    <InfoBox title="Lock vs Mutex — the naming">
      Mutex = Mutual Exclusion. It's the concept. Lock is Python's class that implements it. Other languages call it <code style={{color:T.accent}}>Mutex</code> or <code style={{color:T.accent}}>Monitor</code> — same idea.
    </InfoBox>
    <InterviewQ items={[
      {q:"What is a critical section?",a:"The block of code that accesses shared mutable state — the section that must not be interleaved between threads. The Lock wraps this section so only one thread executes it at a time."},
      {q:"Is threading.Lock() fair? Does it guarantee FIFO for waiting threads?",a:"No. Python's Lock makes no fairness guarantee. A thread that has been waiting longer may not be next. If you need FIFO ordering, use a queue.Queue or implement your own fair lock."},
    ]} />
  </div>
);}

function S9() { return (
  <div>
    <Concept>A deadlock happens when two or more threads wait on each other forever. Thread-1 holds lock1 and waits for lock2. Thread-2 holds lock2 and waits for lock1. Neither can proceed. The fix: always acquire multiple locks in the same fixed order across all threads.</Concept>
    <Analogy>Two people each holding one chopstick, each waiting for the other's chopstick before eating — nobody eats. The fix: agree on a rule — always pick up the left chopstick first, then the right.</Analogy>
    <ASCII>{`Deadlock cycle:
  Thread-1 holds lock1 ──────────────────────────┐
  Thread-1 waits for lock2 (held by Thread-2)    │
                                                  ↓
  Thread-2 holds lock2 ←── Thread-2 waits for lock1
  → CIRCULAR WAIT → nobody moves`}</ASCII>
    <CodeBlock code={`import threading

lock1 = threading.Lock()
lock2 = threading.Lock()

# BAD — opposite acquire order → deadlock risk
def task1():
    with lock1:
        with lock2:          # Task-1: lock1 first, lock2 second
            print("Task1 done")

def task2():
    with lock2:              # Task-2: lock2 first, lock1 second ← REVERSED
        with lock1:
            print("Task2 done")

# GOOD — always same order everywhere
def task1_safe():
    with lock1:
        with lock2:
            print("Task1 done")

def task2_safe():
    with lock1:              # same order as task1_safe
        with lock2:
            print("Task2 done")`} />
    <DeadlockDemo />
    <WarnBox items={[
      "Always acquire multiple locks in the same order everywhere in your codebase.",
      "Use threading.RLock if a locked function must call another locked function in the same thread.",
      "Consider lock hierarchies (assign a rank to each lock; always acquire lower rank first).",
    ]} />
    <InterviewQ items={[
      {q:"What are the four conditions for deadlock (Coffman conditions)?",a:"1) Mutual exclusion — resource held exclusively. 2) Hold-and-wait — thread holds one resource while waiting for another. 3) No preemption — resources can't be forcibly taken. 4) Circular wait — threads form a circular chain of waiting. Break any one condition to prevent deadlock."},
      {q:"How can you detect a deadlock in production?",a:"Thread dumps (Python: faulthandler.dump_traceback()), stack traces showing threads blocked on Lock.acquire(), or external tools like py-spy. Prevention (consistent lock ordering) is almost always better than detection."},
    ]} />
  </div>
);}

function S10() { return (
  <div>
    <Concept>A regular <code style={{color:T.accent}}>Lock</code> cannot be acquired twice by the same thread — the second <code style={{color:T.accent}}>acquire()</code> blocks forever, even from the same thread. An <code style={{color:T.accent}}>RLock</code> (reentrant lock) allows the same thread to acquire it multiple times, as long as it releases the same number of times. Use it when a locked method needs to call another locked method.</Concept>
    <Analogy>A regular Lock is a key that remembers "someone is inside" but not who. A reentrant lock is a key card that remembers who's holding it — the same person can walk through the same door twice without getting stuck, but anyone else still has to wait outside.</Analogy>
    <CodeBlock code={`import threading

# With regular Lock — DEADLOCK:
lock = threading.Lock()

def outer():
    with lock:
        inner()      # tries to acquire lock again — BLOCKS FOREVER

def inner():
    with lock:       # same thread can't re-enter
        print("Inner")

# With RLock — WORKS:
rlock = threading.RLock()

def outer_safe():
    with rlock:         # acquire count = 1
        inner_safe()    # same thread can re-enter

def inner_safe():
    with rlock:         # acquire count = 2
        print("Inner")
    # release → count = 1

# outer_safe exits → count = 0 → truly released`} />
    <InfoBox title="When to use RLock">
      Use RLock when you have a class where multiple methods lock <code style={{color:T.accent}}>self._lock</code> and some methods call other methods of the same class — a very common pattern.
    </InfoBox>
    <InterviewQ items={[
      {q:"Does RLock hurt performance?",a:"Slightly — it has a small overhead to track the owning thread and reentry count. Prefer Lock where reentry isn't needed. Only reach for RLock when you actually need reentrance."},
      {q:"Can Thread-A release an RLock that Thread-B acquired?",a:"No — an RLock can only be released by the thread that acquired it. Releasing from another thread raises RuntimeError."},
    ]} />
  </div>
);}

function S11() { return (
  <div>
    <Concept>A <code style={{color:T.accent}}>Semaphore</code> allows a configurable number of threads to hold it simultaneously — not just one like a Lock. It's used to cap concurrent access: API rate limits, database connection pools, download worker pools.</Concept>
    <Analogy>A room that fits exactly 3 people. A 4th person must wait outside until someone leaves. The semaphore is the door counter — it decrements on entry and increments on exit, blocking newcomers when the count reaches zero.</Analogy>
    <ASCII>{`Semaphore(3) — 3 slots available:
  ┌──────────┐
  │ Thread-A │ ← inside (count=2)
  │ Thread-B │ ← inside (count=1)
  │ Thread-C │ ← inside (count=0)
  └──────────┘
  Thread-D, E … wait until one exits and count > 0`}</ASCII>
    <CodeBlock code={`import threading
import time

semaphore = threading.Semaphore(3)   # max 3 threads at once

def access_db(user):
    with semaphore:
        print(f"{user} connected")
        time.sleep(2)                # simulate DB query
        print(f"{user} disconnected")

threads = [threading.Thread(target=access_db, args=(f"User-{i}",))
           for i in range(10)]

for t in threads: t.start()
for t in threads: t.join()`} />
    <SemaphoreDemo />
    <InfoBox title="BoundedSemaphore">
      Use <code style={{color:T.accent}}>threading.BoundedSemaphore(n)</code> to prevent accidental over-releases — it raises ValueError if release() is called more times than acquire(). Safer for connection pools.
    </InfoBox>
    <InterviewQ items={[
      {q:"What is the difference between a Semaphore and a Lock?",a:"A Lock is a Semaphore(1) — only one thread can hold it. A Semaphore(n) allows up to n threads simultaneously. A Lock also tracks ownership (only the acquiring thread can release it); a Semaphore does not."},
      {q:"When would you use a Semaphore over a ThreadPoolExecutor?",a:"Semaphore is simpler for protecting a shared resource with concurrent access limits. ThreadPoolExecutor is better when you have many tasks to run and want automatic worker management. For connection pools, a Semaphore is often cleaner."},
    ]} />
  </div>
);}

function S12() { return (
  <div>
    <Concept>An <code style={{color:T.accent}}>Event</code> is a thread-safe boolean flag. One or more threads call <code style={{color:T.accent}}>event.wait()</code> and block until another thread calls <code style={{color:T.accent}}>event.set()</code>. It is the cleanest way to say "wait here until something happens" — no shared data needed, just a signal.</Concept>
    <Analogy>A starter's flag at a race. All runners (threads) are ready at the line calling wait(). When the starter fires the gun (set()), all runners are released simultaneously. clear() puts the flag back down for the next race.</Analogy>
    <ASCII>{`                event.wait()  event.wait()  event.wait()
Thread-A ──────────┤              │              │
Thread-B ──────────┤              │              │
Thread-C ──────────┤              │              │
                   ↓ event.set()  ↓              ↓
Thread-D calls set() → all waiting threads unblock at once`}</ASCII>
    <CodeBlock code={`import threading
import time

event = threading.Event()

def waiter(name):
    print(f"{name}: waiting for signal…")
    event.wait()                      # blocks here until set()
    print(f"{name}: signal received! Starting work.")

def signaler():
    time.sleep(2)
    print("Sending start signal")
    event.set()                       # unblocks ALL waiting threads

threads = [threading.Thread(target=waiter, args=(f"Worker-{i}",))
           for i in range(3)]
threads.append(threading.Thread(target=signaler))

for t in threads: t.start()
for t in threads: t.join()`} />
    <CodeBlock label="wait with timeout" code={`# wait() returns True if event was set, False if timeout expired
if not event.wait(timeout=5.0):
    print("Timed out — signal never came")
else:
    print("Signal received")`} />
    <InterviewQ items={[
      {q:"What is the difference between Event and Condition?",a:"Event is a simple flag — set/clear/wait. Condition is more powerful — it can wake one or all waiting threads (notify vs notify_all) and is always used with a Lock for checking shared state safely. Use Event for simple go/no-go signals; Condition for producer-consumer patterns that need finer control."},
    ]} />
  </div>
);}

function S13() { return (
  <div>
    <Concept><code style={{color:T.accent}}>queue.Queue</code> is a thread-safe FIFO. Instead of manually locking a shared list, threads call <code style={{color:T.accent}}>put()</code> to add items and <code style={{color:T.accent}}>get()</code> to remove them — locking is handled internally. This is the standard, recommended way to pass data between threads.</Concept>
    <Analogy>An order ticket rail in a restaurant kitchen. The server clips tickets on (put). The cook pulls the next ticket off (get). Nobody has to negotiate over a shared clipboard — the rail handles the order and exclusion automatically.</Analogy>
    <ASCII>{`Producer thread(s)          Queue (FIFO)          Consumer thread(s)
───────────────────        ──────────────────        ───────────────────
task_A ──── put() ──→  [ task_A | task_B | task_C ] ──→ get() → process
task_B ──── put() ──→                               ──→ get() → process
task_C ──── put() ──→                               ──→ get() → process`}</ASCII>
    <CodeBlock code={`import threading
import queue
import time

q = queue.Queue()

def producer():
    for i in range(5):
        item = f"task-{i}"
        q.put(item)
        print(f"Produced: {item}")
        time.sleep(0.5)
    q.put(None)                   # sentinel: tell consumer to stop

def consumer():
    while True:
        item = q.get()            # blocks until an item is available
        if item is None:
            break
        print(f"Consumed: {item}")
        q.task_done()             # signal that item is processed

t1 = threading.Thread(target=producer)
t2 = threading.Thread(target=consumer)
t1.start(); t2.start()
t1.join(); t2.join()`} />
    <QueueDemo />
    <InfoBox title="Queue variants">
      <code style={{color:T.accent}}>queue.Queue</code> — FIFO (default). <code style={{color:T.accent}}>queue.LifoQueue</code> — LIFO (stack). <code style={{color:T.accent}}>queue.PriorityQueue</code> — items ordered by priority tuple <code style={{color:T.accent}}>(priority, data)</code>. All are thread-safe.
    </InfoBox>
    <InterviewQ items={[
      {q:"Why use Queue instead of a plain list with a Lock?",a:"Queue already handles locking internally and also provides blocking get() (waits until an item is available) and maxsize (blocks put() when full). Reimplementing this correctly with a bare list + Lock + Condition is non-trivial."},
      {q:"What is task_done() and join() on a Queue?",a:"q.task_done() signals that a get()'d item has been processed. q.join() blocks until all items put into the queue have been get()'d and task_done()'d. Useful for wait-until-all-work-done patterns."},
    ]} />
  </div>
);}

function S14() { return (
  <div>
    <Concept>A daemon thread runs in the background and is automatically killed when the main program exits — it doesn't keep the process alive. Regular (non-daemon) threads must finish before the process exits. Use daemon threads for monitoring, heartbeats, or log flushing that shouldn't block shutdown.</Concept>
    <Analogy>Background music in a store. It plays the whole time the store is open, but the moment the store closes, the music stops — nobody waits for the current song to finish. Regular threads are staff who must clock out properly; daemon threads are the speakers that just cut off.</Analogy>
    <CodeBlock code={`import threading
import time

def background_monitor():
    while True:
        print("Heartbeat — still alive")
        time.sleep(1)

t = threading.Thread(target=background_monitor, daemon=True)
t.start()

time.sleep(3)
print("Main program ends")
# daemon thread is killed here — no waiting`} />
    <CodeBlock label="daemon vs regular comparison" code={`import threading, time

def regular():
    time.sleep(5)
    print("Regular thread done")   # will print — main waits for this

def daemon_fn():
    time.sleep(5)
    print("Daemon done")           # may NEVER print — killed on exit

t1 = threading.Thread(target=regular)              # daemon=False (default)
t2 = threading.Thread(target=daemon_fn, daemon=True)

t1.start(); t2.start()
# Program exits 5s later (after t1 finishes); t2 may be killed mid-sleep`} />
    <WarnBox items={[
      "Never use daemon threads for work that must complete — e.g., writing to a database. Data can be lost if the process exits mid-write.",
      "You cannot join() a daemon thread after the main thread exits — set daemon=True only for truly disposable background tasks.",
    ]} />
    <InterviewQ items={[
      {q:"How do you set a thread as daemon?",a:"Pass daemon=True to Thread() constructor, or set t.daemon = True before t.start(). You cannot change daemon status after the thread starts."},
      {q:"Are all threads in a ThreadPoolExecutor non-daemon?",a:"No — threads in a ThreadPoolExecutor are daemon threads by default in Python 3.9+. The executor's __exit__ method calls shutdown(wait=True), which waits for all submitted futures to complete before the context manager exits."},
    ]} />
  </div>
);}

function S15() { return (
  <div>
    <Concept><code style={{color:T.accent}}>ThreadPoolExecutor</code> manages a fixed pool of worker threads — no manual <code style={{color:T.accent}}>start()</code> / <code style={{color:T.accent}}>join()</code> bookkeeping. <code style={{color:T.accent}}>executor.map()</code> runs a function across many inputs and returns results in order. <code style={{color:T.accent}}>submit()</code> returns a Future for finer control.</Concept>
    <Analogy>Instead of hiring and dismissing a courier for every single delivery, you keep a pool of 2 couriers on staff and hand them the next package as they free up. The executor is the dispatch manager — it assigns work and collects results.</Analogy>
    <CodeBlock code={`from concurrent.futures import ThreadPoolExecutor
import time

def download_file(filename):
    time.sleep(2)                          # simulate I/O
    return f"{filename} downloaded"

files = ["report.csv", "model.pkl", "config.json", "data.parquet"]

# executor.map — simple, results in input order
with ThreadPoolExecutor(max_workers=2) as executor:
    results = executor.map(download_file, files)

for r in results:
    print(r)`} />
    <CodeBlock label="submit() + Future for fine control" code={`from concurrent.futures import ThreadPoolExecutor, as_completed
import time

def fetch_url(url):
    time.sleep(1)
    return f"200 OK from {url}"

urls = ["https://api-1.example.com", "https://api-2.example.com",
        "https://api-3.example.com"]

with ThreadPoolExecutor(max_workers=3) as executor:
    futures = {executor.submit(fetch_url, url): url for url in urls}

    for future in as_completed(futures):    # results as they complete (not in order)
        url = futures[future]
        try:
            result = future.result()
            print(f"{url}: {result}")
        except Exception as e:
            print(f"{url}: FAILED — {e}")`} />
    <ASCII>{`max_workers=2, 4 tasks:
Timeline:
  Worker-1: [task-0]──────[task-2]──────
  Worker-2:    [task-1]──────[task-3]──────
                                 ↑ tasks queue up until a worker is free`}</ASCII>
    <InfoBox title="map() vs submit()">
      Use <code style={{color:T.accent}}>map()</code> when you want results in order and all tasks are homogeneous. Use <code style={{color:T.accent}}>submit()</code> + <code style={{color:T.accent}}>as_completed()</code> when you want results as soon as they finish, or need per-task error handling.
    </InfoBox>
    <InterviewQ items={[
      {q:"How do you handle exceptions from a ThreadPoolExecutor?",a:"Exceptions are captured inside the Future. future.result() re-raises the original exception. Wrap future.result() in try/except, or use as_completed() to handle failures per-task."},
      {q:"What happens if max_workers is not set?",a:"Python 3.8+ defaults to min(32, os.cpu_count() + 4). For I/O-bound work, a higher value (e.g., 10–50) is often better because threads spend most time waiting, not computing."},
    ]} />
  </div>
);}

// ─── SECTIONS METADATA ────────────────────────────────────────────────────────
const SECTIONS = [
  {id:"home", icon:"🏠", title:"Overview",             sub:"Start here"},
  {id:"s1",  icon:"🧵", title:"What is a Thread?",    sub:"Thread vs process, start/join"},
  {id:"s2",  icon:"⚡", title:"Why Threading?",        sub:"I/O-bound vs CPU-bound, GIL"},
  {id:"s3",  icon:"🎯", title:"Basic Thread Example",  sub:"start(), join(), args"},
  {id:"s4",  icon:"⏱", title:"Without join()",        sub:"Race to exit, fire-and-forget"},
  {id:"s5",  icon:"🏷", title:"Thread Names",          sub:"Naming, current_thread, enumerate"},
  {id:"s6",  icon:"🏁", title:"Race Condition",        sub:"Read-modify-write, non-atomic ops"},
  {id:"s7",  icon:"🔐", title:"Lock / Mutex",          sub:"Critical section, with lock:"},
  {id:"s8",  icon:"🔑", title:"Lock vs Mutex",         sub:"Concepts, terminology"},
  {id:"s9",  icon:"💀", title:"Deadlock",              sub:"Circular wait, lock ordering"},
  {id:"s10", icon:"🔄", title:"RLock",                 sub:"Reentrant lock, same-thread reentry"},
  {id:"s11", icon:"🚦", title:"Semaphore",             sub:"Bounded concurrency, connection pools"},
  {id:"s12", icon:"🚩", title:"Event",                 sub:"Signalling between threads"},
  {id:"s13", icon:"📦", title:"Queue",                 sub:"Thread-safe FIFO, producer-consumer"},
  {id:"s14", icon:"👻", title:"Daemon Thread",         sub:"Background tasks, auto-kill on exit"},
  {id:"s15", icon:"🏊", title:"ThreadPoolExecutor",    sub:"map(), submit(), Future"},
  {id:"quiz",icon:"📝", title:"Quiz",                  sub:"15 questions — test yourself"},
];

const sectionComponents = {
  s1:S1, s2:S2, s3:S3, s4:S4, s5:S5, s6:S6, s7:S7, s8:S8,
  s9:S9, s10:S10, s11:S11, s12:S12, s13:S13, s14:S14, s15:S15,
  quiz: QuizSection,
};

// ─── HOME PAGE ────────────────────────────────────────────────────────────────
function HomePage({ onNavigate }) {
  return (
    <div>
      <div style={{marginBottom:28}}>
        <div style={{fontSize:11,fontWeight:600,color:T.accent,letterSpacing:2,
          textTransform:"uppercase",marginBottom:10}}>Complete Learning Roadmap</div>
        <h1 style={{fontSize:26,fontWeight:700,color:T.textPri,margin:"0 0 10px",lineHeight:1.3}}>
          Python Threading<br />
          <span style={{color:T.accent}}>Beginner → Advanced</span>
        </h1>
        <p style={{fontSize:14,color:T.textSec,lineHeight:1.7,maxWidth:560}}>
          15 structured sections covering every threading concept — from what a thread is
          to ThreadPoolExecutor. Each section includes analogies, ASCII diagrams, code examples,
          live interactive demos, and interview questions.
        </p>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:28}}>
        {[
          {num:"15",label:"Sections"},
          {num:"4",label:"Live Demos"},
          {num:"30+",label:"Code Examples"},
          {num:"15",label:"Quiz Questions"},
        ].map((stat,i)=>(
          <div key={i} style={{background:T.card,borderRadius:10,padding:"12px 14px",
            border:`1px solid ${T.border}`,textAlign:"center"}}>
            <div style={{fontSize:22,fontWeight:700,color:T.accent}}>{stat.num}</div>
            <div style={{fontSize:11,color:T.textMute,marginTop:2}}>{stat.label}</div>
          </div>
        ))}
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        {SECTIONS.filter(s=>s.id!=="home").map((s,i)=>(
          <button key={s.id} onClick={()=>onNavigate(s.id)}
            style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:10,
              padding:"12px 14px",cursor:"pointer",textAlign:"left",
              transition:"all .15s",fontFamily:"inherit",display:"flex",gap:12,alignItems:"flex-start"}}>
            <span style={{fontSize:18,lineHeight:1.2,minWidth:24}}>{s.icon}</span>
            <div>
              <div style={{fontSize:11,color:T.textMute,marginBottom:2}}>
                {s.id==="quiz" ? "Self-Test" : `Section ${i+1}`}
              </div>
              <div style={{fontSize:13,fontWeight:600,color:T.textPri,marginBottom:2}}>{s.title}</div>
              <div style={{fontSize:11,color:T.textSec}}>{s.sub}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function PythonThreading() {
  const [current, setCurrent] = useState("home");
  const [visited, setVisited] = useState(new Set(["home"]));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const contentRef = useRef(null);

  const navigate = (id) => {
    setCurrent(id);
    setVisited(prev => new Set([...prev, id]));
    if (contentRef.current) contentRef.current.scrollTop = 0;
  };

  const currentIdx = SECTIONS.findIndex(s=>s.id===current);
  const prevSection = currentIdx > 0 ? SECTIONS[currentIdx-1] : null;
  const nextSection = currentIdx < SECTIONS.length-1 ? SECTIONS[currentIdx+1] : null;
  const CurrentComponent = sectionComponents[current];
  const progressPct = Math.round((visited.size / SECTIONS.length) * 100);

  return (
    <div style={{display:"flex",flexDirection:"column",height:"calc(100vh - 70px)",
      background:T.bg,color:T.textPri,fontFamily:"system-ui,-apple-system,sans-serif",overflow:"hidden"}}>

      {/* TOP BAR */}
      <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 16px",
        background:T.surface,borderBottom:`1px solid ${T.border}`,flexShrink:0,zIndex:10}}>
        <button onClick={()=>setSidebarOpen(o=>!o)}
          style={{background:"none",border:"none",color:T.textSec,cursor:"pointer",
            fontSize:18,padding:"2px 4px",lineHeight:1}}>☰</button>
        <span style={{fontSize:15,fontWeight:600,color:T.textPri}}>🧵 Threading Roadmap</span>
        <div style={{flex:1,height:4,background:T.card,borderRadius:2,overflow:"hidden"}}>
          <div style={{width:`${progressPct}%`,height:"100%",background:T.accent,
            borderRadius:2,transition:"width .4s ease"}} />
        </div>
        <span style={{fontSize:12,color:T.accent,minWidth:36,textAlign:"right",fontWeight:600}}>
          {progressPct}%
        </span>
      </div>

      <div style={{display:"flex",flex:1,overflow:"hidden"}}>

        {/* SIDEBAR */}
        {sidebarOpen && (
          <div style={{width:220,minWidth:220,background:T.surface,
            borderRight:`1px solid ${T.border}`,overflowY:"auto",flexShrink:0}}>
            <div style={{padding:"8px 6px"}}>
              {SECTIONS.map((s,i)=>(
                <button key={s.id} onClick={()=>navigate(s.id)}
                  style={{
                    display:"flex",alignItems:"center",gap:8,width:"100%",
                    padding:"7px 10px",border:"none",borderRadius:7,cursor:"pointer",
                    background:current===s.id ? `${T.accent}18` : "none",
                    fontFamily:"inherit",marginBottom:2,
                    borderLeft:current===s.id ? `2px solid ${T.accent}` : "2px solid transparent",
                  }}>
                  <span style={{fontSize:13,minWidth:18}}>{s.icon}</span>
                  <div style={{flex:1,textAlign:"left"}}>
                    <div style={{fontSize:11,
                      color:current===s.id?T.accent:visited.has(s.id)?T.green:T.textSec,
                      fontWeight:current===s.id?600:400,lineHeight:1.3}}>
                      {s.id==="home"||s.id==="quiz" ? s.title : `${i}. ${s.title}`}
                    </div>
                  </div>
                  {visited.has(s.id) && current!==s.id && (
                    <span style={{fontSize:10,color:T.green}}>✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* MAIN CONTENT */}
        <div ref={contentRef} style={{flex:1,overflowY:"auto"}}>
          <div style={{maxWidth:900,margin:"0 auto",padding:"24px 28px"}}>

            {current==="home" ? (
              <HomePage onNavigate={navigate} />
            ) : (
              <div>
                {/* Section header */}
                <div style={{marginBottom:24}}>
                  <div style={{fontSize:11,fontWeight:600,color:T.accent,letterSpacing:1.5,
                    textTransform:"uppercase",marginBottom:8}}>
                    {current==="quiz" ? "Self-Test" : `Section ${currentIdx} of ${SECTIONS.length-2}`}
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                    <span style={{fontSize:24}}>{SECTIONS[currentIdx].icon}</span>
                    <h1 style={{fontSize:22,fontWeight:700,color:T.textPri,margin:0}}>
                      {SECTIONS[currentIdx].title}
                    </h1>
                  </div>
                  <p style={{fontSize:13.5,color:T.textSec,margin:0,lineHeight:1.6}}>
                    {SECTIONS[currentIdx].sub}
                  </p>
                </div>

                {CurrentComponent && <CurrentComponent />}

                {/* Prev / Next navigation */}
                <div style={{display:"flex",justifyContent:"space-between",
                  marginTop:32,paddingTop:20,borderTop:`1px solid ${T.border}`}}>
                  {prevSection ? (
                    <button onClick={()=>navigate(prevSection.id)}
                      style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:9,
                        padding:"10px 16px",cursor:"pointer",color:T.textSec,
                        fontFamily:"inherit",fontSize:13,textAlign:"left"}}>
                      ← {prevSection.icon} {prevSection.title}
                    </button>
                  ) : <div />}
                  {nextSection && (
                    <button onClick={()=>navigate(nextSection.id)}
                      style={{background:`${T.accent}18`,border:`1px solid ${T.accent}44`,
                        borderRadius:9,padding:"10px 16px",cursor:"pointer",color:T.accent,
                        fontFamily:"inherit",fontSize:13,fontWeight:600}}>
                      {nextSection.icon} {nextSection.title} →
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
